// src/components/FileUpload.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { useAppContext } from "../../../libs/contextLib";
import {
  Box,
  Button,
  TextField,
  LinearProgress,
  Alert,
  Paper,
  Typography,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import { rosbagAPI } from "../../../axiosApi";

const ROSBagUpload = ({ onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [error, setError] = useState(null);
  const { selectedItemAppContext, setSelectedItemAppContext } = useAppContext();

  useEffect(() => {
      console.log(selectedItemAppContext.id);
    }, [selectedItemAppContext]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.bag')) {
        setError('ROS bag 파일(.bag)만 업로드 가능합니다.');
        return;
      }
      setSelectedFile(file);
      setFileName(file.name.replace('.bag', ''));
      setError(null);
      setUploadStatus(null);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.bag') || file.name.endsWith('.db3') || file.name.endsWith('.mcap')) {
        setSelectedFile(file);
        setFileName(file.name.replace('.bag', ''));
        setFileName(file.name.replace('.db3', ''));
        setFileName(file.name.replace('.mcap', ''));
        setError(null);
        setUploadStatus(null);
      } else {
        setError('ROS bag 파일(.bag, .mcap, .db3)만 업로드 가능합니다.');
      }
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile || !fileName.trim()) {
      setError('파일과 이름을 모두 입력해주세요.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStatus('uploading');

    const formData = new FormData();
    formData.append('hbscenario', selectedItemAppContext.id);
    formData.append('vehicleinfo', 1);
    formData.append('file', selectedFile);
    formData.append('name', fileName.trim());

    try {
      const response = await rosbagAPI.uploadFile(formData, (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );
        setUploadProgress(progress);
      });

      setUploadStatus('success');
      setSelectedFile(null);
      setFileName('');
      setUploadProgress(0);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setError(
        error.response?.data?.message || 
        '업로드 중 오류가 발생했습니다.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setFileName('');
    setUploadProgress(0);
    setUploadStatus(null);
    setError(null);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        ROS Bag 파일 업로드
      </Typography>

      {/* 파일 드롭 영역 */}
      <Box
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'action.active', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          클릭하거나 파일을 드래그하여 업로드
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ROS bag 파일(.bag)만 지원됩니다
        </Typography>
        
        <input
          id="file-input"
          type="file"
          accept=".bag"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Box>

      {/* 선택된 파일 정보 */}
      {selectedFile && (
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="파일 이름"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            disabled={isUploading}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              선택된 파일: {selectedFile.name} 
              ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
            {!isUploading && (
              <IconButton onClick={handleCancel} size="small">
                <Close />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* 업로드 진행률 */}
      {isUploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            업로드 중... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* 상태 메시지 */}
      {uploadStatus === 'success' && (
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ mb: 2 }}
        >
          파일이 성공적으로 업로드되었습니다. 처리 중입니다...
        </Alert>
      )}

      {error && (
        <Alert 
          severity="error" 
          icon={<Error />}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* 업로드 버튼 */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || !fileName.trim() || isUploading}
          startIcon={<CloudUpload />}
        >
          {isUploading ? '업로드 중...' : '업로드'}
        </Button>
        
        {selectedFile && !isUploading && (
          <Button variant="outlined" onClick={handleCancel}>
            취소
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ROSBagUpload;